import { MapPin, Calendar, Users, Clock, Heart, Briefcase, Cake, Church, Landmark, HardHat, Flag, PartyPopper, Flower2, Building2, Cross, UtensilsCrossed } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const Experiences = () => {
  const { t } = useLanguage();

  const eventsFeatured = [
    {
      name: t('experiences.weddings'),
      description: t('experiences.weddingsDesc'),
      duration: t('experiences.customized'),
      icon: Heart
    },
    {
      name: t('experiences.corporateEvents'),
      description: t('experiences.corporateEventsDesc'),
      duration: t('experiences.customized'),
      icon: Briefcase
    }
  ];

  const events = [
    {
      name: t('experiences.annualEventsList.holyWeek.name'),
      month: t('experiences.annualEventsList.holyWeek.month'),
      description: t('experiences.annualEventsList.holyWeek.description'),
      icon: Cross
    },
    {
      name: t('experiences.annualEventsList.gastronomicFestival.name'),
      month: t('experiences.annualEventsList.gastronomicFestival.month'),
      description: t('experiences.annualEventsList.gastronomicFestival.description'),
      icon: UtensilsCrossed
    },
    {
      name: t('experiences.annualEventsList.anniversary.name'),
      month: t('experiences.annualEventsList.anniversary.month'),
      description: t('experiences.annualEventsList.anniversary.description'),
      icon: Cake
    },
    {
      name: t('experiences.annualEventsList.corpusChristi.name'),
      month: t('experiences.annualEventsList.corpusChristi.month'),
      description: t('experiences.annualEventsList.corpusChristi.description'),
      icon: Church
    },
    {
      name: t('experiences.annualEventsList.tiradentes.name'),
      month: t('experiences.annualEventsList.tiradentes.month'),
      description: t('experiences.annualEventsList.tiradentes.description'),
      icon: Landmark
    },
    {
      name: t('experiences.annualEventsList.laborDay.name'),
      month: t('experiences.annualEventsList.laborDay.month'),
      description: t('experiences.annualEventsList.laborDay.description'),
      icon: HardHat
    },
    {
      name: t('experiences.annualEventsList.independence.name'),
      month: t('experiences.annualEventsList.independence.month'),
      description: t('experiences.annualEventsList.independence.description'),
      icon: Flag
    },
    {
      name: t('experiences.annualEventsList.aparecida.name'),
      month: t('experiences.annualEventsList.aparecida.month'),
      description: t('experiences.annualEventsList.aparecida.description'),
      icon: PartyPopper
    },
    {
      name: t('experiences.annualEventsList.finados.name'),
      month: t('experiences.annualEventsList.finados.month'),
      description: t('experiences.annualEventsList.finados.description'),
      icon: Flower2
    },
    {
      name: t('experiences.annualEventsList.republicProclamation.name'),
      month: t('experiences.annualEventsList.republicProclamation.month'),
      description: t('experiences.annualEventsList.republicProclamation.description'),
      icon: Building2
    },
    {
      name: t('experiences.annualEventsList.blackConsciousness.name'),
      month: t('experiences.annualEventsList.blackConsciousness.month'),
      description: t('experiences.annualEventsList.blackConsciousness.description'),
      icon: Users
    }
  ];

  return (
    <div className="min-h-screen bg-[#f6f6f8] pt-28 pb-20">
      {/* Hero */}
      <div
        className="relative h-[400px] mb-20 parallax"
        style={{
          backgroundImage: 'url("https://lh3.googleusercontent.com/aida-public/AB6AXuD1sW5D1sGStNcpfIqWibGzHyQN1Y0yvgL1JAq_1tvNY9IreSiHriC0YQrDwM4uVyyrq0LOppTvAmYLa1yIlomSkO_L1g6Y1FizPYeRoDb4Mm3FjQviu6rEjgGWLMZZ4ogYZ1ZahvzAJ50FxjZ3YG4uijKjVP8Q5c7Iylwqsn5Rbgf6Zmd89zhb2ILMUL7Z8JHYE4zrMf8vwtNsvWviONxN54gDM85uoL0L7IbFM1QRnvI6V8dQX4qVWiRGLTZUqcdNs72JzN6bRRQ")'
        }}
        data-testid="experiences-hero"
      >
        <div className="absolute inset-0 bg-[#101B2D]/60"></div>
        <div className="relative z-10 h-full flex items-center justify-center">
          <div className="text-center animate-fade-in">
            <h1 className="text-5xl md:text-6xl font-extrabold text-white mb-4">{t('experiences.title')}</h1>
            <p className="text-xl text-gray-200">{t('experiences.subtitle')}</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Experiences */}
        <div className="mb-20">
          <div className="text-center mb-12 animate-slide-up" data-testid="experiences-header">
            <h2 className="text-4xl font-extrabold mb-4 text-slate-900">{t('experiences.toursActivities')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('experiences.toursDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {eventsFeatured.map((item, itemIdx) => (
              <div
                key={itemIdx}
                className="bg-white rounded-xl p-6 shadow-lg hover-scale animate-slide-up"
                style={{ animationDelay: `${itemIdx * 0.1}s` }}
                data-testid={`experience-item-${itemIdx}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <item.icon className="w-6 h-6 text-[#C5A059]" />
                  <h4 className="text-xl font-bold text-slate-900">{item.name}</h4>
                </div>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">{item.description}</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Clock className="w-4 h-4 text-[#C5A059]" />
                    <span>{item.duration}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Events */}
        <div className="mb-12">
          <div className="text-center mb-12 animate-slide-up" data-testid="events-header">
            <h2 className="text-4xl font-extrabold mb-4 text-slate-900">{t('experiences.annualEvents')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('experiences.annualEventsDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {events.map((event, idx) => (
              <div
                key={idx}
                className="bg-white rounded-xl p-6 shadow-lg text-center hover-scale animate-slide-up"
                style={{ animationDelay: `${idx * 0.1}s` }}
                data-testid={`event-card-${idx}`}
              >
                <div className="w-16 h-16 bg-[#C5A059]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <event.icon className="w-8 h-8 text-[#C5A059]" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{event.name}</h3>
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Calendar className="w-4 h-4 text-[#C5A059]" />
                  <span className="text-sm font-semibold text-[#C5A059]">{event.month}</span>
                </div>
                <p className="text-gray-600 text-sm">{event.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center bg-white rounded-2xl p-12 shadow-xl animate-slide-up" data-testid="experiences-cta">
          <h3 className="text-3xl font-bold text-slate-900 mb-4">
            {t('experiences.customizeExperience')}
          </h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            {t('experiences.customizeDesc')}
          </p>
          <a
            href={`https://wa.me/5531991640648?text=${encodeURIComponent(t('whatsapp.experiencesInfo'))}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 btn-gold rounded-lg px-8 py-3 font-bold"
            data-testid="experiences-contact-button"
          >
            {t('experiences.talkToConcierge')}
          </a>
        </div>
      </div>
    </div>
  );
};

export default Experiences;
